/**
 * @file LabelLotPickList.tsx
 * @description Выбор партии при печати с карточки фасовки: радио-карточки
 *   56px и «Без партии» с предупреждением (phase19.22, ui-design §5).
 *
 * Партия — главное на складской этикетке (W17-9a): без неё сборка не сверит
 * партию. Поэтому «Без партии» стоит последним и при выборе объясняет цену
 * решения. Карточки — кнопки `role="radio"`, а не нативное радио: тач-цель.
 */

import { TriangleAlert } from "lucide-react";

import { LabelStatusBadge } from "./LabelStatusBadge";
import { LABEL_TEXT } from "./label-texts";
import { NO_LOT } from "./useSkuModeItems";
import { SectionLabel } from "../../ui/SectionLabel";
import type { SkuLabelLotDto } from "../../../lib/api-labels";
import { formatStockQty } from "../stock/stock-level-format";
import { formatMonthYear } from "../../../lib/receipt-expiry";
import { cn } from "@/lib/utils";

interface Props {
  lots: SkuLabelLotDto[];
  value: string;
  onChange: (value: string) => void;
  loading: boolean;
}

/** «L0000017 · до 05.2027 · 34 шт · A-02-03 · Основной». */
function lotText(lot: SkuLabelLotDto): string {
  return [
    lot.code,
    `до ${formatMonthYear(lot.expiresAt)}`,
    `${formatStockQty(lot.quantity)} шт`,
    lot.bin?.code ?? "без места",
    lot.warehouseName,
  ].join(" · ");
}

function Option({
  checked,
  onSelect,
  children,
}: {
  checked: boolean;
  onSelect: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={checked}
      onClick={onSelect}
      className={cn(
        "flex min-h-14 w-full items-center gap-3 rounded-xl px-3 py-2 text-left text-sm transition-colors",
        checked ? "bg-primary/10 shadow-[inset_0_0_0_2px_var(--primary)]" : "bg-surface-low hover:bg-accent",
      )}
    >
      <span
        aria-hidden
        className={cn(
          "size-5 shrink-0 rounded-full",
          checked ? "border-[6px] border-primary" : "border-2 border-on-surface/30",
        )}
      />
      <span className="flex min-w-0 flex-1 flex-col gap-1">{children}</span>
    </button>
  );
}

export function LabelLotPickList({ lots, value, onChange, loading }: Props) {
  return (
    <div className="flex flex-col gap-2">
      <SectionLabel as="div">Партия</SectionLabel>
      {loading ? <p className="text-sm text-on-surface-variant">Загружаем партии…</p> : null}
      {!loading && lots.length === 0 ? (
        <p className="text-sm text-on-surface-variant">{LABEL_TEXT.noLots}</p>
      ) : null}
      <div role="radiogroup" aria-label="Партия" className="flex flex-col gap-2">
        {lots.map((lot) => (
          <Option key={`${lot.lotId}:${lot.warehouseId}`} checked={value === lot.lotId} onSelect={() => onChange(lot.lotId)}>
            <span className="font-mono text-xs">{lotText(lot)}</span>
            <span className="flex flex-wrap gap-1">
              <LabelStatusBadge status={lot.labelStatus} printedAt={lot.labelsPrintedAt} />
              {lot.expiryMissing ? <span className="text-xs text-warning">Срок: не указан</span> : null}
            </span>
          </Option>
        ))}
        <Option checked={value === NO_LOT} onSelect={() => onChange(NO_LOT)}>
          <span>Без партии</span>
        </Option>
      </div>
      {value === NO_LOT ? (
        <p className="flex items-start gap-1.5 text-xs text-warning">
          <TriangleAlert className="mt-0.5 size-3.5 shrink-0" aria-hidden />
          {LABEL_TEXT.noLotWarning}
        </p>
      ) : null}
    </div>
  );
}
